// components/UserDropdown/UserDropdown.jsx
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "store/auth/thunks";
import { useLanguage } from "context";
import { UserBlackIcon } from "assets/icons";
import {
  ProfileIcon,
  BookingsIcon,
  MySpacesIcon,
  FavoritesIcon,
  ContactIcon,
  LogoutIcon,
} from "./userDropdownIcons";
import styles from "./styles.module.scss";
import { selectIsAuthenticated, selectUserType } from "store/auth/selectors";

const UserDropdown = ({ useWhiteIcons = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslation("global");
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const type = useSelector(selectUserType);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  // Logout handler
  const handleLogout = async () => {
    await dispatch(logout({ language })); // Pass language to the logout thunk
    navigate("/"); // Redirect to home after logout
  };

  return (
    <div className={styles.dropdownContainer} ref={dropdownRef}>
      <button
        className={`${styles.dropdownTrigger} ${isOpen ? styles.active : ""}`}
        onClick={handleToggle}
        aria-label="User menu"
        aria-expanded={isOpen}
      >
        <img
          src={useWhiteIcons ? UserBlackIcon : UserBlackIcon}
          alt="user icon"
        />
      </button>

      {isOpen && (
        <div className={styles.dropdownMenu}>
          <div className={styles.dropdownContent}>
            {/* Profile Link */}
            <button
              className={styles.menuItem}
              onClick={() => handleNavigation("/dashboard")}
            >
              <span className={styles.menuIcon}>
                <ProfileIcon />
              </span>
              <span className={styles.menuText}>
                {t("userDropdown.profile")}
              </span>
            </button>

            {/* Bookings Link */}
            <button
              className={styles.menuItem}
              onClick={() => handleNavigation("/dashboard/bookings")}
            >
              <span className={styles.menuIcon}>
                <BookingsIcon />
              </span>
              <span className={styles.menuText}>
                {t("userDropdown.bookings")}
              </span>
            </button>

            {/* Favorites or My Spaces Link */}
            {isAuthenticated && type === "seeker" ? (
              <button
                className={styles.menuItem}
                onClick={() => handleNavigation("/favorites")}
              >
                <span className={styles.menuIcon}>
                  <FavoritesIcon />
                </span>
                <span className={styles.menuText}>
                  {t("userDropdown.favorites")}
                </span>
              </button>
            ) : (
              null
              // <button
              //   className={styles.menuItem}
              //   onClick={() => handleNavigation("/dashboard/spaces")}
              // >
              //   <span className={styles.menuIcon}>
              //     <MySpacesIcon />
              //   </span>
              //   <span className={styles.menuText}>
              //     {t("userDropdown.spaces")}
              //   </span>
              // </button>
            )}

            {/* Contact us Link */}
            <button
              className={styles.menuItem}
              onClick={() => handleNavigation("/contact")}
            >
              <span className={styles.menuIcon}>
                <ContactIcon />
              </span>
              <span className={styles.menuText}>
                {t("userDropdown.contact")}
              </span>
            </button>

            {/* Divider */}
            <div className={styles.menuDivider}></div>

            {/* Logout Button */}
            <button
              className={`${styles.menuItem} ${styles.logoutItem}`}
              onClick={handleLogout}
            >
              <span className={styles.menuIcon}>
                <LogoutIcon />
              </span>
              <span className={styles.menuText}>
                {t("userDropdown.logout")}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDropdown;